import { useEffect, useRef, useState } from 'react';
import api from '../api';
import { QUALITY_SERVICE_API_URL } from '../config';

/**
 * Generic hook for fetching per-section campaign data.
 * Accepts an optional filters object — non-empty values are appended as query params.
 *
 * Behaviour:
 *  - Switching campaign/section clears data immediately (avoids stale content).
 *  - Changing filters keeps the previous data visible until the new response
 *    arrives, then swaps in one go (no flash-to-empty while refetching).
 */
const useCampaignSection = (campaignId, section, filters = {}) => {
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState(null);

  const filterKey = Object.entries(filters)
    .filter(([, v]) => (Array.isArray(v) ? v.length > 0 : v))
    .flatMap(([k, v]) => (Array.isArray(v) ? v.map(x => `${k}=${x}`) : [`${k}=${v}`]))
    .join('&');

  // Clear data immediately when the campaign or section changes.
  const prevKeyRef = useRef(null);
  const contextKey = `${campaignId}::${section}`;
  if (prevKeyRef.current !== contextKey) {
    prevKeyRef.current = contextKey;
    if (data !== null) setData(null);
  }

  useEffect(() => {
    if (!campaignId) return;

    let cancelled = false;
    setLoading(true);
    setError(null);

    const params = new URLSearchParams();
    Object.entries(filters).forEach(([k, v]) => {
      if (Array.isArray(v)) v.forEach(x => params.append(k, x));
      else if (v) params.append(k, v);
    });
    const qs = params.toString() ? `?${params.toString()}` : '';

    api.get(`${QUALITY_SERVICE_API_URL}/hub/campaigns/${campaignId}/${section}${qs}`)
      .then(r  => { if (!cancelled) setData(r.data); })
      .catch(e => { if (!cancelled) setError(e); })
      .finally(() => { if (!cancelled) setLoading(false); });

    return () => { cancelled = true; };
  }, [campaignId, section, filterKey]); // eslint-disable-line react-hooks/exhaustive-deps

  return { data, loading, error };
};

export default useCampaignSection;
